import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemButton,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { PopupDialog, LookupRadio, LookupListItem, TextFieldComponent } from '../../../../components/common';
import { TipoIdentificacionSelect } from '../../../../components/parametros/TipoIdentificacionSelect';
import { GeneroSelect } from '../../../../components/parametros/GeneroSelect';
import { type ProveedorFiltered, type ProveedorRequest } from '../types/proveedor';
import { useProveedorStore } from '../store';
import { ProveedorService } from '../services/proveedorService';

interface ProveedorSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (proveedor: ProveedorFiltered) => void;
  title?: string;
}

const ProveedorSelectionModal: React.FC<ProveedorSelectionModalProps> = ({
  open,
  onClose,
  onSelect,
  title = 'Seleccionar Proveedor'
}) => {
  const [selectedProveedor, setSelectedProveedor] = useState<ProveedorFiltered | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newRazonSocial, setNewRazonSocial] = useState('');
  const [newIdentificacion, setNewIdentificacion] = useState('');
  const [newIdTipoIdentificacion, setNewIdTipoIdentificacion] = useState<number | string>('');
  const [newIdGenero, setNewIdGenero] = useState<number | string>('');
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
  const [isCreating, setIsCreating] = useState(false);

  const {
    filteredProveedores,
    loading,
    error,
    fetchFilteredProveedores,
    clearError
  } = useProveedorStore();

  useEffect(() => {
    if (open) {
      fetchFilteredProveedores();
      setSelectedProveedor(null);
    }
  }, [open, fetchFilteredProveedores]);

  const handleSelect = () => {
    if (selectedProveedor) {
      onSelect(selectedProveedor);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedProveedor(null);
    onClose();
  };

  const handleProveedorClick = (proveedor: ProveedorFiltered) => {
    setSelectedProveedor(proveedor);
  };

  const handleCloseError = () => {
    clearError();
  };

  const handleOpenCreateDialog = () => {
    setNewRazonSocial('');
    setNewIdentificacion('');
    setNewIdTipoIdentificacion('');
    setNewIdGenero('');
    setCreateErrors({});
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  const handleCreateProveedor = async () => {
    const errors: Record<string, string> = {};
    if (!newRazonSocial.trim()) errors.razonSocial = 'La razón social es requerida';
    if (!newIdentificacion.trim()) errors.identificacion = 'La identificación es requerida';
    if (!newIdTipoIdentificacion) errors.idTipoIdentificacion = 'El tipo de identificación es requerido';
    if (!newIdGenero) errors.idGenero = 'El género es requerido';

    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);
      return;
    }

    setIsCreating(true);
    try {
      const request: ProveedorRequest = {
        razonSocial: newRazonSocial.trim(),
        identificacion: newIdentificacion.trim(),
        idTipoIdentificacion: Number(newIdTipoIdentificacion),
        idGenero: Number(newIdGenero),
      };

      const saved = await ProveedorService.saveProveedor(request);
      await fetchFilteredProveedores();

      const newFiltered: ProveedorFiltered = {
        idPersona: saved.idPersona,
        razonSocial: saved.razonSocial,
        identificacion: saved.identificacion,
      };
      onSelect(newFiltered);
      setCreateDialogOpen(false);
      handleClose();
    } catch {
      setCreateErrors({ razonSocial: 'Error al crear el proveedor' });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <PopupDialog
        open={open}
        onClose={handleClose}
        title={title}
        maxWidth="md"
        primaryAction={{
          label: 'Seleccionar',
          onClick: handleSelect,
          disabled: !selectedProveedor || loading,
          loading: loading,
          variant: 'contained',
          color: 'primary'
        }}
        secondaryAction={{
          label: 'Cancelar',
          onClick: handleClose,
          variant: 'outlined',
          color: 'inherit'
        }}
      >
        {error && (
          <Alert
            severity="error"
            onClose={handleCloseError}
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
            size="small"
          >
            Nuevo Proveedor
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ minHeight: 300, maxHeight: 400, overflow: 'auto' }}>
            {filteredProveedores.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                No hay proveedores disponibles
              </Typography>
            ) : (
              <List>
                {filteredProveedores.map((proveedor, index) => (
                  <React.Fragment key={proveedor.idPersona}>
                    <ListItem disablePadding>
                      <ListItemButton
                        sx={{ p: 0 }}
                        onClick={() => handleProveedorClick(proveedor)}
                        selected={selectedProveedor?.idPersona === proveedor.idPersona}
                      >
                        <LookupRadio
                          checked={selectedProveedor?.idPersona === proveedor.idPersona}
                          value={proveedor.idPersona}
                        />
                        <LookupListItem
                          mainText={`${proveedor.idPersona} - ${proveedor.razonSocial}`}
                          secondaryText={proveedor.identificacion}
                          sx={{ justifyContent: 'space-between' }}
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < filteredProveedores.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        )}
      </PopupDialog>

      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo Proveedor</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextFieldComponent
                  label="Razón Social"
                  value={newRazonSocial}
                  onChange={setNewRazonSocial}
                  error={createErrors.razonSocial}
                  required
                  fullWidth
                  disabled={isCreating}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextFieldComponent
                  label="Identificación"
                  value={newIdentificacion}
                  onChange={setNewIdentificacion}
                  error={createErrors.identificacion}
                  required
                  fullWidth
                  disabled={isCreating}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TipoIdentificacionSelect
                  label="Tipo Identificación"
                  value={newIdTipoIdentificacion}
                  onChange={(val) => {
                    setNewIdTipoIdentificacion(val);
                    if (createErrors.idTipoIdentificacion) {
                      setCreateErrors(prev => ({ ...prev, idTipoIdentificacion: '' }));
                    }
                  }}
                  error={createErrors.idTipoIdentificacion}
                  required
                  disabled={isCreating}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <GeneroSelect
                  label="Género"
                  value={newIdGenero}
                  onChange={(val) => {
                    setNewIdGenero(val);
                    if (createErrors.idGenero) {
                      setCreateErrors(prev => ({ ...prev, idGenero: '' }));
                    }
                  }}
                  error={createErrors.idGenero}
                  required
                  disabled={isCreating}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog} disabled={isCreating}>
            Cancelar
          </Button>
          <Button onClick={handleCreateProveedor} variant="contained" disabled={isCreating}>
            {isCreating ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProveedorSelectionModal;
